import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { apiRequest, refreshAccessToken, setAccessToken, setSessionListener } from "../lib/api";
import { clearAttribution, storedAttribution } from "../lib/attribution";
import type { ApiEnvelope, User } from "../types";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  register(fullName: string, email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  /** Adopts a session established outside the app, such as a social sign-in callback. */
  refresh(): Promise<boolean>;
  /** Applies a user the API returned, so `hasPassword` updates without another round trip. */
  applyUser(user: User): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Bumped every time a session is established or dropped on purpose, so the slower
  // startup restore can recognise that its own result is stale and stop applying it.
  const sessionEpoch = useRef(0);

  const applySession = useCallback((nextUser: User | null, token: string | null) => {
    sessionEpoch.current += 1;
    setAccessToken(token);
    setUser(nextUser);
    setLoading(false);
  }, []);

  const loadUser = useCallback(async () => {
    const epoch = sessionEpoch.current;
    const token = await refreshAccessToken();
    if (sessionEpoch.current !== epoch) return;
    if (!token) { setUser(null); return; }
    try {
      const payload = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/auth/me");
      if (sessionEpoch.current !== epoch) return;
      setUser(payload.data.user);
    } catch {
      if (sessionEpoch.current !== epoch) return;
      setUser(null);
      setAccessToken(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = setSessionListener((token) => { if (!token) setUser(null); });
    void loadUser().finally(() => setLoading(false));
    return unsubscribe;
  }, [loadUser]);

  const login = useCallback(async (email: string, password: string) => {
    const payload = await apiRequest<ApiEnvelope<{ user: User; accessToken: string }>>("/api/v1/auth/login", {
      method: "POST", body: JSON.stringify({ email, password }), retryAuth: false
    });
    applySession(payload.data.user, payload.data.accessToken);
  }, [applySession]);

  const register = useCallback(async (fullName: string, email: string, password: string) => {
    const payload = await apiRequest<ApiEnvelope<{ user: User; accessToken: string }>>("/api/v1/auth/register", {
      method: "POST", body: JSON.stringify({ fullName, email, password, attribution: storedAttribution() }), retryAuth: false
    });
    clearAttribution();
    applySession(payload.data.user, payload.data.accessToken);
  }, [applySession]);

  const logout = useCallback(async () => {
    await apiRequest<void>("/api/v1/auth/logout", { method: "POST", retryAuth: false }).catch(() => undefined);
    applySession(null, null);
  }, [applySession]);

  const refresh = useCallback(async () => {
    const token = await refreshAccessToken();
    if (!token) { applySession(null, null); return false; }
    try {
      const payload = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/auth/me");
      applySession(payload.data.user, token);
      return true;
    } catch {
      applySession(null, null);
      return false;
    }
  }, [applySession]);

  const applyUser = useCallback((next: User) => setUser(next), []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, applyUser }),
    [user, loading, login, register, logout, refresh, applyUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
